import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import {
  unit_all,
  unit_full,
  unit_option_full,
  unit_option_group_full,
} from '@Api';
import { Unit, UnitCarac, UnitCaracType } from '@Models';
import { UnitOptionMapper } from './unit-option.mapper';
import { ModelRuleUnitTroopMapper } from './ModelRuleUnitTroopMapper';
import { OrganisationChangeMapper } from './OrganisationChangeMapper';
import { UnitCaracMapper } from './UnitCaracMapper';
import { TroopMapper } from './TroopMapper';
import { ConstraintMapper } from '@Mapper/ninthAge/constraint.mapper';
import { ConstraintModifierMapper } from '@Mapper/ninthAge/constraint-modifier.mapper';
import { UnitOptionGroup } from '@Models';

@injectable()
export class UnitMapper {
  @inject(TYPES.UnitOptionMapper) private _unitOptionMapper!: UnitOptionMapper;
  @inject(TYPES.Mapper.ModelRuleUnitTroopMapper)
  private _modelRuleUnitTroopMapper!: ModelRuleUnitTroopMapper;
  @inject(TYPES.OrganisationChangeMapper)
  private _organisationChangeMapper!: OrganisationChangeMapper;
  @inject(TYPES.TroopMapper) private _troopMapper!: TroopMapper;
  @inject(TYPES.UnitCaracMapper) private _unitCaracMapper!: UnitCaracMapper;
  @inject(TYPES.Mapper.ConstraintMapper)
  private _constraintMapper!: ConstraintMapper;
  @inject(TYPES.Mapper.ConstraintModifierMapper)
  private _constraintModifierMapper!: ConstraintModifierMapper;

  public map(item: unit_full): Unit {
    return {
      id: item.id,
      army_id: item.army_id,
      name: item.name ?? '',
      add_value_points: item.add_value_points,
      points: item.value_points,
      position: item.position,
      is_mount: item.is_mount,
      hidden: item.hidden ?? false,
      is_desabled: item.is_desabled,
      organisationIds: item.organisation_ids,
      allOrganisationIds: item.all_organisation_ids,
      magic: item.magic,
      min_size: item.min_size,
      max_size: item.max_size,
      notes: item.notes,
      optionGroups: this.mapOptionGroups(
        item.unit_option_groups,
        item.unit_options,
      ),
      modelRuleUnitTroops: item.model_rule_unit_troops.map((item) =>
        this._modelRuleUnitTroopMapper.map(item),
      ),
      organisation_changes: item.organisation_changes.map((item) =>
        this._organisationChangeMapper.map(item),
      ),
      principal_organisation_id: item.principal_organisation_id,
      armyOrganisationId: item.army_organisation_id,
      armyOrganisationActivatorId: item.army_organisation_activator_id,
      unitCategoryId: item.unit_category_id,
      troops: item.troops.map((item) => this._troopMapper.map(item)),
      type_figurine: item.type_figurine,
      carac: this._unitCaracMapper.map(item.carac),
      constraints:
        item.constraints?.map((item) => this._constraintMapper.map(item)) ?? [],
      modifiers:
        item.modifiers?.map((item) =>
          this._constraintModifierMapper.map(item),
        ) ?? [],
    } satisfies Unit as Unit;
  }

  public mapAll(item: unit_all): Unit {
    return {
      id: item.id,
      army_id: 0,
      name: item.name ?? '',
      add_value_points: 0,
      points: 0,
      position: 1,
      is_mount: false,
      hidden: false,
      is_desabled: false,
      allOrganisationIds: [],
      armyOrganisationId: null,
      magic: null,
      min_size: 1,
      max_size: 1,
      notes: null,
      unitCategoryId: 0,
      optionGroups: [],
      modelRuleUnitTroops: [],
      organisation_changes: [],
      principal_organisation_id: 0,
      armyOrganisationActivatorId: null,
      troops: [],
      type_figurine: 0,
      carac: {
        type: UnitCaracType.V2,
        ground_adv: null,
        ground_mar: null,
        dis: null,
        evoked: null,
        hp: null,
        def: null,
        res: null,
        arm: null,
        aeg: null,
        fly_adv: null,
        fly_mar: null,
        unitBase: {
          id: '1',
          name: '20x20mm',
        },
        unitHeight: {
          id: '1',
          name: 'Standard',
        },
        unitType: null,
      } satisfies UnitCarac as UnitCarac,
      organisationIds: [],
      constraints: [],
      modifiers: [],
    } satisfies Unit as Unit;
  }

  private mapOptionGroups(
    unit_option_groups: unit_option_group_full[],
    unit_options: unit_option_full[],
  ): UnitOptionGroup[] {
    return unit_option_groups.map((item) => {
      const options = unit_options.filter(
        (option) => option.unit_option_group_id === item.id,
      );
      return {
        id: item.id,
        name: item.name,
        position: item.position,
        options: options.map((option) => this._unitOptionMapper.map(option)),
        constraints:
          item.constraints?.map((item) => this._constraintMapper.map(item)) ??
          [],
        modifiers:
          item.modifiers?.map((item) =>
            this._constraintModifierMapper.map(item),
          ) ?? [],
        hidden: item.hidden,
      } satisfies UnitOptionGroup as UnitOptionGroup;
    });
  }
}
